import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import Stepper from './Stepper';
import RarityBadge from '@/components/RarityBadge';
import { ORDER_STATUS, CANCELLATION_REASON, getOrderStatusStepIndex, isOrderCancelled, getOrderMessageTitleKey, getOrderExplanationKey, getCancellationReasonKey } from '@/config/orderStatus';
import purchasedIcon from '@/assets/status_purchased.svg';
import sellerShippingIcon from '@/assets/status_seller_shipping.svg';
import receivedIcon from '@/assets/status_received.svg';
import verifyingIcon from '@/assets/status_verifying.svg';
import shippingIcon from '@/assets/status_shipping.svg';
import canceledIcon from '@/assets/status_canceled.svg';
import completedIcon from '@/assets/status_done.svg';
import closeIcon from '@/assets/close.svg';
import copyIcon from '@/assets/copy.svg';
import { formatDate, formatCurrency, formatPhoneNumber } from '@/utils/formatUtils';
import OfficialTag from '@/components/OfficialTag';
import { getProductThumbnailUrl } from '@/utils/imageUtils';

const OrderDetailsModal = ({ isOpen, onClose, order, onConfirmReceipt }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!isOpen || !order) return null;
  
  // Helper to get the modal title based on order status
  const getModalTitle = (status, isOfficial) => {
    if (isOfficial && getOrderStatusStepIndex(status, isOfficial) === 0) {
      return t(`orders.modalTitles.${ORDER_STATUS.PAID.toLowerCase()}`);
    } else {
      return t(`orders.modalTitles.${status.toLowerCase()}`);
    }
  };

  // Helper to get the message title based on order status
  const getMessageTitle = (status, isOfficial) => {
    return t(getOrderMessageTitleKey(status, isOfficial));
  };

  // Helper to get the explanation based on order status
  const isCancelled = isOrderCancelled(order.status);
  const getExplanation = (status, isOfficial, cancellationReason, verificationNote) => {
    return isCancelled ? cancellationReason === CANCELLATION_REASON.VERIFICATION_FAILED && verificationNote ? t(getCancellationReasonKey(cancellationReason, verificationNote), {reason: verificationNote}) : t(getCancellationReasonKey(cancellationReason, verificationNote)) : t(getOrderExplanationKey(status, isOfficial));
  };

  // Helper to format status steps
  const getStatusStep = (status, isOfficial) => {
    const currentStepIndex = getOrderStatusStepIndex(status, isOfficial);
    
    // Define the stepper labels in order
    const stepLabels = isOfficial ? [
      t('orders.status.paid'),
      t('orders.status.warehouse_shipped'),
      t('orders.status.completed')
    ] : [
      t('orders.status.paid'),
      t('orders.status.seller_shipped'),
      t('orders.status.warehouse_received'),
      t('orders.status.verifying'),
      t('orders.status.warehouse_shipped'),
      t('orders.status.completed')
    ];
    return stepLabels.map((label, index) => ({
      label: label,
      completed: index <= currentStepIndex,
      active: index === currentStepIndex,
    }));
  };

  const getStatusIcon = (status, isOfficial) => {
    if (status != ORDER_STATUS.CANCELLED && isOfficial && getOrderStatusStepIndex(status, isOfficial) === 0) {
      return purchasedIcon;
    } else {
      switch (status) {
        case ORDER_STATUS.PAID:
          return purchasedIcon;
        case ORDER_STATUS.SELLER_SHIPPED:
          return sellerShippingIcon;
        case ORDER_STATUS.WAREHOUSE_RECEIVED:
          return receivedIcon;
        case ORDER_STATUS.VERIFYING:
          return verifyingIcon;
        case ORDER_STATUS.WAREHOUSE_SHIPPED:
          return shippingIcon;
        case ORDER_STATUS.COMPLETED:
          return completedIcon;
        case ORDER_STATUS.CANCELLED:
          return canceledIcon;
        default:
          return null;
      }
    }
  }

  const statusIcon = getStatusIcon(order.status, order.listing?.isOfficial);
  const statusSteps = getStatusStep(order.status, order.listing?.isOfficial);
  const modalTitle = getModalTitle(order.status, order.listing?.isOfficial);
  const messageTitle = getMessageTitle(order.status, order.listing?.isOfficial);
  const explanation = getExplanation(order.status, order.listing?.isOfficial, order.cancellationReason, order.verificationNote);
  
  // Check if the order is in shipped status
  const isShipped = order.status === ORDER_STATUS.WAREHOUSE_SHIPPED;

  // Function to copy tracking number to clipboard
  const copyTrackingNumber = () => {
    if (order.warehouseShipment?.trackingNumber) {
      navigator.clipboard.writeText(order.warehouseShipment.trackingNumber);
      // You could add a toast notification here if you have one in your app
    }
  };

  const handleConfirmReceiptClick = () => {
    if (onConfirmReceipt) {
      onClose();
      onConfirmReceipt();
    }
  };

  const handleContactClick = () => {
    onClose();
    navigate('/contact');
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex flex-col justify-center items-end !border-t-0 z-50">
      <div className="w-[480px] h-full bg-white flex flex-col items-center gap-6 p-6 overflow-y-auto">
        {/* Header */}
        <div className="w-full flex flex-cols items-center justify-between">
            <div className='flex items-center'>
                {statusIcon && (
                    <img src={statusIcon} alt={order.status} className="w-4 h-4" />
                )}
                <h2 className="text-lg font-bold ml-2">
                    {modalTitle}
                </h2>
            </div>
            <button 
                onClick={onClose}
                className="w-6 h-6 flex items-center justify-center"
            >
                <img src={closeIcon} alt="close" className="w-7 h-7" />
            </button>
        </div>

        {/* Product Image and Info */}
        <div className="w-full flex gap-4">
            <div className="w-32 h-32 rounded overflow-hidden">
              <img 
              src={getProductThumbnailUrl(order.listing.product.images?.[0]?.url)} 
              alt={order.listing?.product?.localizedName || "Product"} 
              className="w-full h-full object-contain"
              />
            </div>
            <div className="flex-1">
                <div className="mb-1">
                  <div className="min-w-0">
                    {order.listing?.isOfficial && (
                      <OfficialTag className="align-middle mr-1.5" />
                    )}
                    <h3 className="inline align-middle text-base font-medium whitespace-normal break-words min-w-0">
                      {order.listing?.product?.localizedRawName || order.listing?.product?.localizedName || ""}
                      {order.listing?.product?.rarityInfo?.sub?.name ? `(${order.listing.product.rarityInfo.sub.name})` : ''}
                    </h3>
                  </div>
                  <div className="flex flex-wrap items-center gap-1 mt-1">
                    {order.listing?.product?.rarityInfo?.main && (
                      <RarityBadge rarity={order.listing.product.rarityInfo.main} />
                    )}
                    {(order.listing?.product?.set?.code || (order.listing?.product?.rawCode && order.listing?.product?.type !== 'CARD_BOX')) && (
                      <div className="h-[18px] px-1 bg-[#f4f4f4] rounded-[3px] outline outline-[0.50px] outline-offset-[-0.50px] outline-[#dddddd] flex justify-start items-center gap-2.5">
                        <div className="justify-start text-[#111111] text-sm font-normal leading-[14px]">
                          {order.listing?.product?.type === 'CARD_BOX' ? order.listing?.product?.set?.code : (order.listing?.product?.set?.code && order.listing?.product?.rawCode ? `${order.listing.product.set.code} ${order.listing.product.rawCode}` : order.listing?.product?.set?.code || order.listing?.product?.rawCode)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-sm mt-6">
                    <span className="text-gray-500">
                        {order.listing?.psa ?
                            t('psa.grade') :
                            order.listing?.bundledQuantity ?
                                t('box.quantity') :
                                t('listing.condition')}:
                    </span>
                    <span className="ml-2">
                        {order.listing?.psa ?
                            order.listing.psa.replace('PSA_', 'PSA ') :
                            order.listing?.bundledQuantity ?
                                (() => {
                                    const unit = order.listing.product?.boxType === 'PACK' ? t('box.pack') : t('box.box_unit');
                                    return `${order.listing.bundledQuantity} ${unit}`;
                                })() :
                                order.listing?.condition ?
                                    t(`sell.product_info.conditions.${order.listing.condition.toLowerCase()}`) :
                                    '-'}
                    </span>
                </div>
            </div>
        </div>

        {/* Status Steps - Using the new Stepper component - Hide for cancelled orders */}
        {!isCancelled && (
          <div className="w-full">
            <Stepper steps={statusSteps} />
          </div>
        )}

        {/* Order Status Message */}
        <div className="w-full p-5 border border-gray-200 bg-[#f8f8f8] rounded-md">
            {!isShipped ? (
                <>
                    <h3 className="text-xl font-bold mb-2 text-center">{messageTitle}</h3>
                    <p className="text-sm text-gray-500 text-center">{explanation}</p>
                </>
            ) : (
                <div className="flex flex-col justify-start items-center gap-5">
                    <div className="self-stretch flex flex-col justify-start items-center gap-2">
                        <h3 className="text-lg font-bold text-[#111111]">{messageTitle}</h3>
                        <p className="text-sm text-[#666666] text-center">{explanation}</p>
                        
                        <div className="self-stretch inline-flex justify-between items-center mt-2">
                            <div className="flex justify-center items-center gap-2">
                                <span className="text-[#666666] text-sm">{t('orders.shippingCompany')}:</span>
                                <span className="text-[#111111] text-sm">{order.warehouseShipment?.courierName}</span>
                            </div>
                            <div className="flex justify-center items-center gap-2">
                                <span className="text-[#666666] text-sm">{t('orders.trackingNumber')}:</span>
                                <span className="text-[#111111] text-sm">{order.warehouseShipment?.trackingNumber || '-'}</span>
                                <button onClick={copyTrackingNumber} className="w-3 h-3 flex items-center justify-center">
                                    <img src={copyIcon} alt="copy" className="w-[11px] h-[11px]" />
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    <Button
                        variant="primary"
                        size="l"
                        className="self-stretch"
                        onClick={handleConfirmReceiptClick}
                    >
                        {t('orders.confirmReceipt.title')}
                    </Button>
                </div>
            )}
        </div>

        {/* Price Information */}
        <div className="w-full gap-4 text-[#111111]">
            <div className="flex justify-between items-center mb-3">
                <span className="text-sm">{t('orders.productPrice')}</span>
                <span className="text-sm">{formatCurrency(order.monetary?.price || 0)}</span>
            </div>
            {order.monetary?.serviceFee > 0 && (<div className="flex justify-between items-center mb-3">
                <span className="text-sm">{t('orders.serviceFee')}</span>
                <span className="text-sm">{formatCurrency(order.monetary?.serviceFee || 0)}</span>
            </div>)}
            <div className="flex justify-between items-center mb-3">
                <span className="text-sm">{t('orders.shippingFee')}</span>
                <span className="text-sm">{formatCurrency(order.monetary?.shippingFee || 0)}</span>
            </div>
            <div className="flex justify-between items-center mb-3">
                <span className="text-sm">{t('orders.authenticationFee')}</span>
                <span className="text-sm">{formatCurrency(order.monetary?.verificationFee || 0)}</span>
            </div>
            <div className="flex justify-between items-center font-bold py-2">
                <span className="text-base">{t('orders.totalAmount')}</span>
                <span className="text-base">{formatCurrency(order.monetary?.total || 0)}</span>
            </div>
        </div>

        <div className='w-full h-[1px] bg-[#f4f4f4]' />

        {/* Order Information */}
        <div className="w-full">
            <h3 className="text-sm font-bold mb-4">{t('orders.orderContents')}</h3>
            <div className="space-y-2">
                <div className="flex">
                    <span className="text-sm text-gray-500 w-36">{t('orders.orderNumber')}</span>
                    <span className="text-sm">{order.number || '-'}</span>
                </div>
                <div className="flex">
                    <span className="text-sm text-gray-500 w-36">{t('orders.transactionDate')}</span>
                    <span className="text-sm">
                        {order.createdAt ? formatDate(order.createdAt) : '-'}
                    </span>
                </div>
                {order.finishedAt && (
                    <div className="flex">
                        <span className="text-sm text-gray-500 w-36">{t('common.finishDate')}</span>
                        <span className="text-sm">
                            {formatDate(order.finishedAt)}
                        </span>
                    </div>
                )}
                <div className="flex">
                    <span className="text-sm text-gray-500 w-36">{t('orders.paymentMethod')}</span>
                    <span className="text-sm">{order.payment?.maskedCardNumber || '-'}</span>
                </div>
            </div>
        </div>

        <div className='w-full h-[1px] bg-[#f4f4f4]' />

        {/* Delivery Information */}
        <div className="w-full">
            <h3 className="text-sm font-bold mb-4">{t('orders.deliveryInfo')}</h3>
            <div className="space-y-2">
                <div className="flex">
                <span className="text-sm text-gray-500 w-36">{t('orders.recipient')}</span>
                <span className="text-sm flex-1">
                    {`${order.shippingAddress?.lastname} ${order.shippingAddress?.firstname}`}
                </span>
                </div>
                <div className="flex">
                <span className="text-sm text-gray-500 w-36">{t('orders.address')}</span>
                <span className="text-sm flex-1">
                    {`${order.shippingAddress?.postalCode} ${order.shippingAddress?.prefecture} ${order.shippingAddress?.city} ${order.shippingAddress?.address1} ${order.shippingAddress?.address2}`}
                </span>
                </div>
                <div className="flex">
                <span className="text-sm text-gray-500 w-36">{t('orders.phoneNumber')}</span>
                <span className="text-sm flex-1">{formatPhoneNumber(order.shippingAddress?.contactNumber)}</span>
                </div>
            </div>
        </div>

        <Button
            onClick={handleContactClick}
            variant="default"
            size="l"
            className="py-2"
        >
            {t('orders.questionOrder')}
        </Button>
      </div>
    </div>
  );
};

export default OrderDetailsModal; 
